import { marked } from "marked";
import { useState } from "react";
import { useSupabaseCallback } from "../../server/supabase/hooks";
import type { ISO8601 } from "../../utils/iso8601";
import type { UUID } from "../../utils/uuid";

type ClickwrapFetchStatus = "idle" | "loading" | "loaded" | "error";

export type ClickwrapTerms = {
  status: ClickwrapFetchStatus;
  title: string;
  contentHtml: string;
  contentFormat: string;
  errorMessage: string | null;
  hasAcceptedCurrentVersion: boolean;
  fetchClickwrapTerms: () => Promise<void>;
  acceptClickwrapTerms: () => Promise<void>;
};

export const useClickwrapTerms = (key: string): ClickwrapTerms => {
  const [status, setStatus] = useState<ClickwrapFetchStatus>("idle");
  const [title, setTitle] = useState("");
  const [contentHtml, setContentHtml] = useState("");
  const [contentFormat, setContentFormat] = useState("");
  const [contentText, setContentText] = useState("");
  const [agreementId, setAgreementId] = useState<UUID | null>(null);
  const [agreementVersionId, setAgreementVersionId] = useState<UUID | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasAcceptedCurrentVersion, setHasAcceptedCurrentVersion] = useState(false);

  const fetchClickwrapTerms = useSupabaseCallback(
    async ({ supabase }) => {
      setStatus("loading");
      setErrorMessage(null);
      setHasAcceptedCurrentVersion(false);
      try {
        const { data: agreement, error: agreementError } = await supabase
          .from("clickwrap_agreement")
          .select("id,title,current_version_id")
          .eq("key", key)
          .single();

        if (agreementError) throw agreementError;
        if (!agreement?.current_version_id) {
          throw new Error("Current agreement version was not found.");
        }

        setTitle(agreement.title);
        const { data: existingAcceptance, error: acceptanceError } = await supabase
          .from("account_clickwrap_acceptance")
          .select("id")
          .eq("agreement_id", agreement.id)
          .eq("agreement_version_id", agreement.current_version_id)
          .is("revoked_at", null)
          .limit(1);
        if (acceptanceError) throw acceptanceError;

        if (existingAcceptance.length > 0) {
          setAgreementId(agreement.id);
          setAgreementVersionId(agreement.current_version_id);
          setHasAcceptedCurrentVersion(true);
          setStatus("loaded");
          return;
        }

        const { data: version, error: versionError } = await supabase
          .from("clickwrap_agreement_version")
          .select("content_format,content_text")
          .eq("agreement_id", agreement.id)
          .eq("id", agreement.current_version_id)
          .single();

        if (versionError) throw versionError;
        if (!version?.content_text) throw new Error("Agreement content was not found.");

        const parsedHtml = await marked.parse(version.content_text);
        const sanitizedHtml = parsedHtml; //This is not user-generated;

        setAgreementId(agreement.id);
        setAgreementVersionId(agreement.current_version_id);
        setContentHtml(sanitizedHtml);
        setContentFormat(version.content_format);
        setContentText(version.content_text);
        setHasAcceptedCurrentVersion(false);
        setStatus("loaded");
      } catch (err) {
        console.error("Failed to fetch clickwrap terms", err);
        setAgreementId(null);
        setAgreementVersionId(null);
        setContentHtml("");
        setContentFormat("");
        setContentText("");
        setHasAcceptedCurrentVersion(false);
        setErrorMessage(
          "Couldn't load the Development Partnership addendum. Please retry to continue.",
        );
        setStatus("error");
      }
    },
    [key],
  );

  const acceptClickwrapTerms = useSupabaseCallback(
    async ({ supabase }) => {
      if (!agreementId || !agreementVersionId || !contentFormat || !contentText) {
        throw new Error("Clickwrap terms are not loaded.");
      }

      const { error } = await supabase.from("account_clickwrap_acceptance").insert({
        accepted_at: new Date().toISOString() as ISO8601,
        agreement_id: agreementId,
        agreement_version_id: agreementVersionId,
        snapshot_content_format: contentFormat,
        snapshot_content_text: contentText,
      });

      if (error) throw error;
      setHasAcceptedCurrentVersion(true);
    },
    [agreementId, agreementVersionId, contentFormat, contentText],
  );

  return {
    status,
    title,
    contentHtml,
    contentFormat,
    errorMessage,
    hasAcceptedCurrentVersion,
    fetchClickwrapTerms,
    acceptClickwrapTerms,
  };
};
